import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml, SafeUrl } from '@angular/platform-browser';
import { unitLabel, routeLabel, timingLabel, intervalUnitLabel, frequencyLabel } from '../prescription-lookups';
import { SignatureService } from '../../signature.service';

@Component({
  standalone: false,
  selector: 'app-prescription-print',
  templateUrl: './prescription-print.component.html',
  styleUrls: ['./prescription-print.component.scss']
})
export class PrescriptionPrintComponent implements OnInit {

  prescription: any = null;
  items: any[] = [];
  lang = 'he';
  dir = 'rtl';
  loaded = false;
  printDate = '';
  env: any = null;
  altHeader: SafeHtml = null;
  doctorSignatureUrl: SafeUrl = null;

  labels = {
    he: {
      title: 'מרשם רפואי',
      name: 'שם',
      id: 'ת.ז.',
      dob: 'תאריך לידה',
      hmo: 'קופת חולים',
      date: 'תאריך',
      drug: 'תרופה',
      activeIngredient: 'חומר פעיל',
      dosage: 'מינון',
      unit: 'יחידה',
      route: 'דרך מתן',
      timing: 'אופן מתן',
      frequency: 'תדירות',
      intervalValue: 'משך',
      intervalUnit: 'יחידת זמן',
      instructions: 'הוראות',
      signedBy: 'נחתם ע"י',
      licenseNum: 'מספר רישיון',
      expertNum: 'מספר רישיון מומחה',
      confidential: 'חסוי רפואי',
      narcoticTitle: 'מרשם נרקוטי',
      doctorId: 'ת"ז רופא'
    },
    en: {
      title: 'Medical Prescription',
      name: 'Name',
      id: 'ID',
      dob: 'Date of Birth',
      hmo: 'Health Provider',
      date: 'Date',
      drug: 'Drug',
      activeIngredient: 'Active Ingredient',
      dosage: 'Dosage',
      unit: 'Unit',
      route: 'Route',
      timing: 'Administration Instructions',
      frequency: 'Frequency',
      intervalValue: 'Duration',
      intervalUnit: 'Duration Unit',
      instructions: 'Instructions',
      signedBy: 'Signed by',
      licenseNum: 'License ',
      expertNum: 'Expert License ',
      confidential: 'CONFIDENTIAL MEDICAL INFO',
      narcoticTitle: 'Narcotic Prescription',
      doctorId: 'Doctor ID'
    }
  };

  private hmoMap: { [key: string]: { he: string; en: string } } = {
    'Clalit':    { he: 'כללית',   en: 'Clalit' },
    'Macabi':    { he: 'מכבי',    en: 'Maccabi' },
    'Meuhedet':  { he: 'מאוחדת', en: 'Meuhedet' },
    'Leumit':    { he: 'לאומית',  en: 'Leumit' },
    'Private':   { he: 'פרטי',    en: 'Private' },
    'IDF':       { he: 'צבאי',    en: 'IDF' },
  };

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private signatureService: SignatureService
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.params['id'];
    const langParam = this.route.snapshot.params['lang'];
    const code = this.route.snapshot.params['code'];
    this.lang = langParam === 'he-IL' ? 'he' : (langParam || 'he');
    this.dir = this.lang === 'he' ? 'rtl' : 'ltr';
    const now = new Date();
    const locale = this.lang === 'he' ? 'he-IL' : 'en-US';
    this.printDate = now.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

    const loadData = () => {
      this.http.get<any>('/api/getenv').subscribe(
        envData => {
          this.env = envData;
          if (envData['htmlheader']) {
            this.altHeader = this.sanitizer.bypassSecurityTrustHtml(envData['htmlheader']);
          }
        },
        () => {}
      );
      this.http.get<any>(`/api/prescriptions/single/${id}`).subscribe(
        res => {
          this.prescription = res;
          this.items = res.items || [];
          this.loaded = true;
          if (res.SignedAt && res.UID) {
            this.loadDoctorSignature(res.UID.toString());
          }
        },
        () => { this.loaded = true; }
      );
    };

    if (code) {
      this.http.post('/api/prescriptions/resurection', { code }).subscribe(
        () => loadData(),
        () => loadData()
      );
    } else {
      loadData();
    }
  }

  private titleMap: { [key: string]: string } = {
    'מר': 'Mr.',
    'גברת': 'Ms.',
    'ד"ר': 'Dr.',
    'פרופסור': 'Prof.',
    'פרופ\'': 'Prof.',
    'פרופ': 'Prof.',
    'Dr.': 'Dr.',
    'Prof.': 'Prof.',
    'Mr.': 'Mr.',
    'Ms.': 'Ms.',
    'Mrs.': 'Mrs.',
  };

  private loadDoctorSignature(uid: string) {
    this.signatureService.checkSignatureExists(uid).subscribe(
      (blob: Blob) => {
        if (blob && blob.size > 0) {
          this.doctorSignatureUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
        }
      },
      () => {}
    );
  }

  getSignerDisplay(prescription: any): string {
    if (!prescription) return '';
    const isEn = this.lang === 'en';
    const name = isEn && prescription.OpenerEngName ? prescription.OpenerEngName : (prescription.Opener || '');
    const rawTitle = prescription.OpenerTitle || '';
    const title = isEn ? (this.titleMap[rawTitle] || rawTitle) : rawTitle;
    return title ? title + ' ' + name : name;
  }

  getHMO(hmo: string): string {
    if (!hmo) return '';
    const entry = this.hmoMap[hmo];
    if (!entry) return hmo;
    return this.lang === 'en' ? entry.en : entry.he;
  }

  get lbl() {
    return this.labels[this.lang] || this.labels['he'];
  }

  formatDate(d: string): string {
    if (!d) return '';
    const locale = this.lang === 'he' ? 'he-IL' : 'en-US';
    return new Date(d).toLocaleDateString(locale);
  }

  formatDateTime(d: string): string {
    if (!d) return '';
    const locale = this.lang === 'he' ? 'he-IL' : 'en-US';
    return new Date(d).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  }

  unitLabel(code: string): string { return unitLabel(code, this.lang); }
  routeLabel(code: string): string { return routeLabel(code, this.lang); }
  timingLabel(code: string): string { return timingLabel(code, this.lang); }
  intervalUnitLabel(code: string): string { return intervalUnitLabel(code, this.lang); }
  frequencyLabel(item: any): string {
    if (!item) return '';
    if (item.FrequencyCustom) return item.FrequencyCustom;
    return frequencyLabel(item.FrequencyCode, this.lang);
  }
}
